import React, { useMemo } from "react";
import { logger } from "@/lib/logger";
import { useFTSSearchWithFullRows } from "@/lib/hooks/api/search/use-fts-search-with-full-rows";
import { searchModeForEntity } from "@/lib/search/embedded-search-entities";
import { enhanceSearchWithFTS } from "@/lib/utils/crm/fts-search-utils";
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";

/**
 * Entity types that support full-text search via `useFTSSearchEnhancer`.
 * Mirrors the `fieldMap` in `useFTSEnhancement` (UniversalEntityPage hook).
 */
export const FTS_ENTITY_TYPES = [
  "contacts",
  "companies",
  "deals",
  "tickets",
  "tasks",
  "communications",
  "offerings",
  "recurring-transactions",
  "recurring-charges",
  "admin_users",
  "social_media_posts",
] as const;

export type FTSEntityType = (typeof FTS_ENTITY_TYPES)[number];

/**
 * Type guard for FTSEntityType — use at call sites with a generic
 * `entityName: string` to avoid `as any` casts.
 */
export function isFTSEntityType(value: string): value is FTSEntityType {
  return (FTS_ENTITY_TYPES as readonly string[]).includes(value);
}

export interface FTSSearchEnhancerProps<TData extends BaseEntity> {
  /**
   * Enable FTS search for this entity
   */
  enableFTS: boolean;

  /**
   * Entity type for FTS search
   */
  entityType?: FTSEntityType;
  
  /**
   * Current search term from the table
   */
  searchTerm: string;
  
  /**
   * Data to search
   */
  data: TData[];
  
  /**
   * Searchable field IDs
   */
  searchableFields: string[];
  
  /**
   * Callback when search results are updated
   */
  onSearchResults?: (results: TData[]) => void;
  
  /**
   * Cache TTL in seconds (default: 1 hour)
   */
  cacheTtl?: number;
}

/**
 * Hook that enhances table search with FTS capabilities
 * Implements industry-standard two-step pattern:
 * 1. FTS search returns IDs + relevance (fast, indexed)
 * 2. Fetch full rows by ID with caching (preserves relevance order)
 * 
 * Automatically falls back to client-side search if FTS is disabled
 * Includes debouncing to prevent excessive API calls
 * 
 * Usage in entity pages:
 * const enhancedData = useFTSSearchEnhancer({
 *   enableFTS: true,
 *   entityType: 'deals',
 *   searchTerm: filters.search,
 *   data: rawData,
 *   searchableFields: ['title', 'description', 'company_name'],
 *   cacheTtl: 3600, // 1 hour
 * });
 */
export function useFTSSearchEnhancer<TData extends BaseEntity>({
  enableFTS,
  entityType,
  searchTerm,
  data,
  searchableFields,
  onSearchResults,
  cacheTtl = 3600,
}: FTSSearchEnhancerProps<TData>) {
  // Ensure entityType is always defined for the hook
  const resolvedEntityType: FTSEntityType = entityType || "contacts";
  
  const { 
    search, 
    results: ftsResults, 
    isLoading: ftsLoading, 
    error: ftsError, 
    totalCount,
    cacheStats,
  } = useFTSSearchWithFullRows({
    entityType: resolvedEntityType,
    limit: 1000, // Fetch up to 1000 results for client-side filtering
    debounceMs: 300, // Debounce search to prevent excessive API calls
    cacheTtl,
    // Embedded entities (contact/company/deal/task/communication) use hybrid semantic
    // search; every other entity stays keyword. Centralized in the allowlist.
    searchMode: searchModeForEntity(resolvedEntityType),
  });

  // Perform FTS search when search term changes (debounced internally)
  React.useEffect(() => {
    if (enableFTS && entityType && searchTerm && searchTerm.trim().length > 0) {
      // search() is debounced internally, so this won't cause excessive API calls
      search(searchTerm);
    } else if (enableFTS && entityType && (!searchTerm || searchTerm.trim().length === 0)) {
      // Clear results when search term is empty
      search("");
    }
  }, [enableFTS, entityType, searchTerm, search]);

  // Enhance data with FTS results
  const enhancedData = useMemo(() => {
    const results = enhanceSearchWithFTS(data, searchTerm, searchableFields, {
      enableFTS,
      entityType,
      ftsResults: ftsResults,
      ftsLoading,
      ftsError,
    });

    onSearchResults?.(results);
    return results;
  }, [data, searchTerm, searchableFields, enableFTS, entityType, ftsResults, ftsLoading, ftsError, onSearchResults]);

  // Log cache stats in development
  React.useEffect(() => {
    if (cacheStats && process.env.NODE_ENV === "development") {
      logger.debug("FTS Cache", { cached: cacheStats.cached, fetched: cacheStats.fetched });
    }
  }, [cacheStats]);

  return {
    data: enhancedData,
    isLoading: ftsLoading,
    error: ftsError,
    totalCount, // Return the total count from FTS results
    cacheStats, // Return cache statistics for monitoring
  };
}
